import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { LogIn } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { viderHorsLigne } from '../../lib/horsLigne.ts';
import { Alerte, Bouton, Champ } from '../../components/ui.tsx';

export function PageConnexion() {
  const [email, setEmail] = useState('');
  const [mdp, setMdp] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const nav = useNavigate();
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const [etape, setEtape] = useState<'identifiants' | 'code'>(params.get('etape') === 'code' ? 'code' : 'identifiants');
  const [code, setCode] = useState('');
  const allerApres = async () => {
    // Nouvelle session : rien de la session précédente (ou d'un autre compte) ne reste sur l'appareil.
    await viderHorsLigne().catch(() => {});
    await qc.invalidateQueries({ queryKey: ['moi'] });
    const retour = params.get('retour');
    nav(retour && retour.startsWith('/') && !retour.startsWith('//') ? retour : '/', { replace: true });
  };
  const verifierCode = async (e: FormEvent) => {
    e.preventDefault();
    setErreur(null); setEnvoi(true);
    try { await api.post('/api/auth/mfa/verifier', { code }); await allerApres(); }
    catch (err) { setErreur(messageErreur(err)); setCode(''); }
    finally { setEnvoi(false); }
  };

  const soumettre = async (e: FormEvent) => {
    e.preventDefault();
    setErreur(null); setEnvoi(true);
    try {
      const r = await api.post<{ mfaRequis: boolean }>('/api/auth/connexion', { email, motDePasse: mdp });
      if (r.mfaRequis) { setMdp(''); setEtape('code'); return; }
      await allerApres();
    } catch (err) {
      setErreur(messageErreur(err));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="connexion">
      <div className="connexion-plan" aria-hidden>
        <svg viewBox="0 0 600 600" preserveAspectRatio="xMidYMid slice">
          <defs>
            <pattern id="g" width="24" height="24" patternUnits="userSpaceOnUse"><path d="M24 0H0V24" fill="none" stroke="var(--grille-plan)" strokeWidth="1" /></pattern>
          </defs>
          <rect width="600" height="600" fill="url(#g)" />
          {/* Coupe d'un immeuble : niveaux, gaine technique et bus qui les relie */}
          <g fill="none" stroke="var(--trait-plan)" strokeWidth="2">
            <path d="M120 520V150l180-70 180 70v370" />
            <path d="M120 230h360M120 310h360M120 390h360M120 470h360" />
          </g>
          <g stroke="var(--accent)" strokeWidth="2.5" fill="none">
            <path d="M300 520V96" />
            <path d="M300 190h120M300 270h-120M300 350h140M300 430h-110M300 495h90" />
          </g>
          <g fill="var(--accent)">
            {[[420, 190], [180, 270], [440, 350], [190, 430], [390, 495], [300, 96]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r="6" />)}
          </g>
        </svg>
      </div>
      <main className="connexion-panneau">
        <div className="pile-l" style={{ width: '100%', maxWidth: 380 }}>
          <div className="pile" style={{ gap: 6 }}>
            <span className="marque-nom" style={{ fontSize: 'var(--t-xl)' }}>TRAME</span>
            <h1 style={{ fontSize: 'var(--t-xl)' }}>Atelier technique du bâtiment connecté</h1>
            <p className="secondaire">Diagnostic sourcé, analyse de dossier avant devis, conception et chiffrage.</p>
          </div>
          {etape === 'code' ? (
            <form className="pile" onSubmit={verifierCode} noValidate>
              {erreur && <Alerte ton="danger">{erreur}</Alerte>}
              <Champ libelle="Code de votre application d’authentification" inputMode="numeric" autoComplete="one-time-code" maxLength={6} mono autoFocus
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} aide="6 chiffres, renouvelés toutes les 30 secondes." />
              <Bouton type="submit" variante="principal" chargement={envoi} disabled={code.length !== 6}>Valider le code</Bouton>
              <p className="tres-petit discret">Téléphone perdu : l’administrateur de votre société peut réinitialiser la double authentification.</p>
            </form>
          ) : (
          <form className="pile" onSubmit={soumettre} noValidate>
            {erreur && <Alerte ton="danger">{erreur}</Alerte>}
            <Champ libelle="Adresse e-mail professionnelle" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
            <Champ libelle="Mot de passe" type="password" autoComplete="current-password" required value={mdp} onChange={(e) => setMdp(e.target.value)} />
            <Bouton type="submit" variante="principal" chargement={envoi} icone={<LogIn aria-hidden />}>Se connecter</Bouton>
            <p className="tres-petit discret">Compte oublié ou bloqué : demandez à l’administrateur de votre société de le réinitialiser.</p>
          </form>
          )}
        </div>
      </main>
    </div>
  );
}
